import React from 'react';
import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Record, RecordSection } from './types';
import { AccordionRecordSection } from './AccordionRecordSection';

const DEFAULT_ITEMS_TO_SHOW = 3;

interface RecordsCardProps {
  sections: RecordSection[];
  title?: string;
  type?: 'single' | 'multiple';
  collapsible?: boolean;
  defaultValue?: string | string[];
  maxItemsPerSection?: number;
  showViewAll?: boolean;
  onSectionViewAll?: (sectionTitle: string) => void;
  onRecordClick?: (record: Record) => void;
  className?: string;
}

const RecordsCard: React.FC<RecordsCardProps> = ({
  sections,
  title,
  type = 'multiple',
  collapsible = true,
  defaultValue,
  maxItemsPerSection = DEFAULT_ITEMS_TO_SHOW,
  showViewAll = true,
  onSectionViewAll,
  onRecordClick,
  className = '',
}) => {
  const validSections = sections.filter(s => s && s.title && s.records);
  const accordionDefaultValue = defaultValue ?? (type === 'multiple' ? validSections.map(s => s.title) : undefined);

  return (
    <div className={cn('bg-white shadow-lg rounded-xl', className)}>
      {title && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
      )}
      
      <div className="p-4">
        <Accordion
          type={type}
          collapsible={collapsible}
          defaultValue={accordionDefaultValue}
          className="space-y-2"
        >
          {validSections.map((section) => (
            <AccordionItem
              key={section.title}
              value={section.title}
              className="border border-gray-200 bg-white shadow-sm rounded-lg"
            >
              <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 rounded-t-lg">
                <div className="flex items-center justify-between w-full mr-4">
                  <h3 className="text-base font-semibold text-gray-900 text-left">
                    {section.title}
                  </h3>
                  {section.records.length > 0 && (
                    <span className="text-sm text-gray-500 ml-2">
                      {section.records.length} {section.records.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <AccordionRecordSection
                  records={section.records}
                  maxItems={section.maxItems ?? maxItemsPerSection}
                  showViewAll={section.showViewAll ?? showViewAll}
                  sectionTitle={section.title}
                  onViewAll={onSectionViewAll}
                  onRecordClick={onRecordClick}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

RecordsCard.displayName = 'RecordsCard';

export { RecordsCard };
export type { RecordsCardProps };
